"use client";

import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import type { QueueContextShape } from "@packages/sdk";
import { snakeCase } from "@packages/sdk";
import type { RefObject } from "react";
import { useEffect, useMemo, useRef } from "react";

import { getContentId } from "../lib/contentIdentification";
import type { MediaElementsRef } from "../types/MediaElementsRef.types";
import { useCurrentQueueItem } from "./useCurrentQueueItem";
import { usePlayer } from "./usePlayer";

export type UseMediaAnalyticsProps = {
  mediaRef?: RefObject<MediaElementsRef>;
  backgroundTitle?: string;
};

type SimpleAnalyticsFunction = () => Promise<void>;

type MediaAnalytics = {
  audioStartDelayed: SimpleAnalyticsFunction;
  audioStarted: SimpleAnalyticsFunction;
  draggedTimeSlider: SimpleAnalyticsFunction;
  loopChanged: (loop: "none" | "one" | "all") => Promise<void>;
  maximizedPlayer: SimpleAnalyticsFunction;
  mediaAnalyticsProps: Record<string, any>;
  minimizedPlayer: SimpleAnalyticsFunction;
  playerBeganTrack: SimpleAnalyticsFunction;
  playerEndedTrack: (
    cause: "complete" | "error" | "cancel" | "timer",
  ) => Promise<void>;
  playerInitialized: SimpleAnalyticsFunction;
  refBasedProps: () => Record<string, any>;
  tappedFastForward: SimpleAnalyticsFunction;
  tappedPause: (player_state: "maximized" | "minimized") => Promise<void>;
  tappedPlay: (player_state: "maximized" | "minimized") => Promise<void>;
  tappedResumeSession: SimpleAnalyticsFunction;
  tappedRewind: SimpleAnalyticsFunction;
  tappedSkipBack: SimpleAnalyticsFunction;
  tappedSkipForward: SimpleAnalyticsFunction;
  tappedSkipIntro: SimpleAnalyticsFunction;
  timerEnded: (timerLength: string) => Promise<void>;
};

export const useMediaAnalytics = ({
  mediaRef,
  backgroundTitle,
}: UseMediaAnalyticsProps = {}): RefObject<MediaAnalytics> => {
  const analytics = useAnalytics();
  const { currentItem, queue } = useCurrentQueueItem();
  const { subtitleIdx } = usePlayer();
  const returnRef = useRef<MediaAnalytics | null>(null);

  const mediaAnalyticsProps = useMemo<Record<string, any>>(
    () => ({
      audio: currentItem?.selected_audio?.id,
      background_music: backgroundTitle ? snakeCase(backgroundTitle) : "none",
      content_id: getContentId(currentItem),
      content_type: currentItem?.type,
      downloaded: false,
      guides: currentItem?.selected_audio?.guide_ids,
      id: currentItem?.selected_audio?.id,
      length: currentItem
        ? snakeCase(currentItem.selected_audio.length)
        : undefined,
      loop: queue?.repeating,
      online: true,
      shuffle: queue?.shuffled,
      subtitles_enabled: subtitleIdx !== null && subtitleIdx !== undefined,
    }),
    [currentItem, queue, subtitleIdx],
  );

  const refBasedProps = () => ({
    percent_complete: mediaRef?.current
      ? mediaRef.current.currentTime / mediaRef.current.duration
      : undefined,
    speed: mediaRef?.current ? mediaRef.current.playbackRate : 1,
  });

  const playerEndedTrack = (cause: "complete" | "error" | "cancel" | "timer") =>
    analytics.track({
      event: HallowAnalyticsEvent.PlayerEndedTrack,
      properties: {
        cause,
        ...mediaAnalyticsProps,
        ...refBasedProps(),
      },
    });

  const playerInitialized = () =>
    analytics.track({
      event: HallowAnalyticsEvent.PlayerInitialized,
    });

  const audioStarted = () =>
    analytics.track({
      event: HallowAnalyticsEvent.AudioStarted,
      properties: { ...mediaAnalyticsProps, ...refBasedProps() },
    });

  const audioStartDelayed = () =>
    analytics.track({
      event: HallowAnalyticsEvent.AudioStartDelayed,
      properties: { ...mediaAnalyticsProps, ...refBasedProps() },
    });

  const draggedTimeSlider = () =>
    analytics.track({
      event: HallowAnalyticsEvent.DraggedTimeSlider,
      properties: { ...mediaAnalyticsProps, ...refBasedProps() },
    });

  const tappedFastForward = () =>
    analytics.track({
      event: HallowAnalyticsEvent.TappedFastForward,
      properties: { ...mediaAnalyticsProps, ...refBasedProps() },
    });

  const tappedRewind = () =>
    analytics.track({
      event: HallowAnalyticsEvent.TappedRewind,
      properties: { ...mediaAnalyticsProps, ...refBasedProps() },
    });

  const tappedSkipForward = () =>
    analytics.track({
      event: HallowAnalyticsEvent.TappedSkipForward,
      properties: { ...mediaAnalyticsProps, ...refBasedProps() },
    });

  const tappedSkipBack = () =>
    analytics.track({
      event: HallowAnalyticsEvent.TappedSkipBack,
      properties: { ...mediaAnalyticsProps, ...refBasedProps() },
    });

  const loopChanged = (loop: QueueContextShape["repeating"]) =>
    analytics.track({
      event: HallowAnalyticsEvent.LoopChanged,
      properties: {
        ...mediaAnalyticsProps,
        ...refBasedProps(),
        loop,
      },
    });

  const tappedSkipIntro = () =>
    analytics.track({
      event: HallowAnalyticsEvent.TappedSkipIntro,
      properties: {
        ...mediaAnalyticsProps,
        ...refBasedProps(),
        collection:
          currentItem?.type === "prayer" ? currentItem.collection.id : null,
      },
    });

  const tappedResumeSession = () =>
    analytics.track({
      event: HallowAnalyticsEvent.TappedResumeSession,
      properties: {
        ...mediaAnalyticsProps,
        ...refBasedProps(),
        resumed_at: currentItem.selected_audio.resumes_at,
      },
    });

  const playerBeganTrack = () =>
    analytics.track({
      event: HallowAnalyticsEvent.PlayerBeganTrack,
      properties: {
        ...mediaAnalyticsProps,
        ...refBasedProps(),
        collection:
          currentItem.type === "prayer" ? currentItem.collection.id : null,
      },
    });

  const maximizedPlayer = () =>
    analytics.track({
      event: HallowAnalyticsEvent.MaximizedPlayer,
      properties: { ...mediaAnalyticsProps, ...refBasedProps() },
    });

  const minimizedPlayer = () =>
    analytics.track({
      event: HallowAnalyticsEvent.MinimizedPlayer,
      properties: { ...mediaAnalyticsProps, ...refBasedProps() },
    });

  const tappedPause = (player_state: "maximized" | "minimized") =>
    analytics.track({
      event: HallowAnalyticsEvent.TappedPause,
      properties: {
        ...mediaAnalyticsProps,
        ...refBasedProps(),
        player_state,
      },
    });

  const tappedPlay = (player_state: "maximized" | "minimized") =>
    analytics.track({
      event: HallowAnalyticsEvent.TappedPlay,
      properties: {
        ...mediaAnalyticsProps,
        ...refBasedProps(),
        player_state,
      },
    });

  const timerEnded = (timerLength: string) =>
    analytics.track({
      event: HallowAnalyticsEvent.TimerEnded,
      properties: {
        ...mediaAnalyticsProps,
        ...refBasedProps(),
        length: snakeCase(timerLength),
      },
    });

  useEffect(() => {
    returnRef.current = {
      audioStartDelayed,
      audioStarted,
      draggedTimeSlider,
      loopChanged,
      maximizedPlayer,
      mediaAnalyticsProps,
      minimizedPlayer,
      playerBeganTrack,
      playerEndedTrack,
      playerInitialized,
      refBasedProps,
      tappedFastForward,
      tappedPause,
      tappedPlay,
      tappedResumeSession,
      tappedRewind,
      tappedSkipBack,
      tappedSkipForward,
      tappedSkipIntro,
      timerEnded,
    };
  }, [
    analytics?.track,
    audioStarted,
    audioStartDelayed,
    draggedTimeSlider,
    loopChanged,
    maximizedPlayer,
    mediaAnalyticsProps,
    minimizedPlayer,
    playerBeganTrack,
    playerEndedTrack,
    playerInitialized,
    refBasedProps(),
    tappedFastForward,
    tappedPause,
    tappedPlay,
    tappedResumeSession,
    tappedRewind,
    tappedSkipBack,
    tappedSkipForward,
    tappedSkipIntro,
    timerEnded,
  ]);

  return returnRef;
};
